import * as React from 'react';
import { Icon } from '@/ui';
import { useToast } from '../ui';

const MAX_BYTES = 5 * 1024 * 1024;
const OK_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif', 'image/svg+xml'];

interface Props {
  file: File | null;
  existingUrl: string; // URL of already-saved image ('' if none)
  onFile: (f: File | null) => void;
  onClearExisting: () => void;
}

export function ImageDropzone({ file, existingUrl, onFile, onClearExisting }: Props) {
  const toast = useToast();
  const [dragging, setDragging] = React.useState(false);
  const [preview, setPreview] = React.useState<string>('');
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreview(existingUrl);
    return undefined;
  }, [file, existingUrl]);

  const accept = (f: File | undefined) => {
    if (!f) return;
    if (!OK_TYPES.includes(f.type)) { toast.show('Nur Bilddateien (JPG, PNG, WebP, GIF, AVIF, SVG).', 'error'); return; }
    if (f.size > MAX_BYTES) { toast.show('Bild ist zu groß (max. 5 MB).', 'error'); return; }
    onFile(f);
  };

  const hasImage = !!preview;

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); accept(e.dataTransfer.files?.[0]); }}
        onClick={() => inputRef.current?.click()}
        style={{
          position: 'relative', border: `2px dashed ${dragging ? 'var(--primary-200)' : 'var(--border-default)'}`,
          borderRadius: 'var(--radius-md)', background: dragging ? 'rgba(74,144,226,0.06)' : 'var(--surface-page)',
          minHeight: hasImage ? 0 : 150, cursor: 'pointer', overflow: 'hidden',
          display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
        }}
      >
        {hasImage ? (
          <img src={preview} alt="Vorschau" style={{ width: '100%', maxHeight: 260, objectFit: 'cover', display: 'block' }} />
        ) : (
          <div style={{ padding: 24, color: 'var(--text-200)' }}>
            <span style={{ display: 'inline-flex', color: 'var(--primary-200)' }}><Icon name="image-up" size={28} /></span>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-100)', marginTop: 8 }}>Bild hierher ziehen</div>
            <div style={{ fontSize: 12.5, marginTop: 3 }}>oder klicken zum Auswählen · JPG/PNG/WebP · max. 5 MB</div>
          </div>
        )}
      </div>
      <input ref={inputRef} type="file" accept={OK_TYPES.join(',')} hidden onChange={(e) => { accept(e.target.files?.[0] ?? undefined); e.target.value = ''; }} />
      {hasImage && (
        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button type="button" onClick={() => inputRef.current?.click()} style={miniBtn}>
            <Icon name="repeat-2" size={14} /> Ersetzen
          </button>
          <button type="button" onClick={() => { if (file) onFile(null); else onClearExisting(); }} style={{ ...miniBtn, color: '#e5484d' }}>
            <Icon name="trash-2" size={14} /> Entfernen
          </button>
        </div>
      )}
    </div>
  );
}

const miniBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', fontSize: 13,
  border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-page)',
  color: 'var(--text-200)', cursor: 'pointer', fontFamily: 'var(--font-sans)',
};
